import i18next from "i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { chartSeriesColors, colors } from "Infrastructure/Themes/Colors";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useReportingGenerationsQuery } from "Reporting/Api/Queries/useReportingGenerationsQuery";
import { ChartCard } from "./ChartCard";
import { ChartEmptyState } from "./ChartEmptyState";

const CHART_COLORS = chartSeriesColors;
const c = colors();

const getMonthShort = (month: number): string => {
  const date = new Date(2024, month - 1, 1);
  return new Intl.DateTimeFormat(i18next.language, { month: "short" }).format(
    date,
  );
};

const getMonthFull = (month: number, year: number): string => {
  const date = new Date(year, month - 1, 1);
  return new Intl.DateTimeFormat(i18next.language, {
    month: "long",
    year: "numeric",
  }).format(date);
};

interface MonthBucket {
  shortLabel: string;
  fullLabel: string;
  [model: string]: string | number;
}

export const GenerationsChart = (): React.ReactNode => {
  const { t } = useResource();
  const { data, isLoading } = useReportingGenerationsQuery();

  const rawItems = data?.avgGenerationsPerJobByModel ?? [];

  // Collect unique voice models
  const models = [...new Set(rawItems.map((i) => i.voiceLlm))];

  // Group by year+month and pivot models into columns
  const bucketMap = new Map<string, MonthBucket>();
  for (const item of rawItems) {
    const key = `${item.year}-${item.month}`;
    if (!bucketMap.has(key)) {
      bucketMap.set(key, {
        shortLabel:
          item.month === 1
            ? `${getMonthShort(1)} ${item.year}`
            : getMonthShort(item.month),
        fullLabel: getMonthFull(item.month, item.year),
      });
    }
    const bucket = bucketMap.get(key)!;
    bucket[item.voiceLlm] = item.avgCount;
  }

  const chartData = [...bucketMap.values()];
  const isEmpty = chartData.length === 0;

  return (
    <ChartCard
      title={t(Resources.Reporting.AvgGenerationsPerJob)}
      isLoading={isLoading}
      height={350}
    >
      {isEmpty ? (
        <ChartEmptyState />
      ) : (
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={chartData}>
            <CartesianGrid stroke={c.Border} strokeDasharray="3 3" />
            <XAxis
              dataKey="shortLabel"
              tick={{ fill: c.TextSecondary, fontSize: 12 }}
              axisLine={{ stroke: c.Border }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: c.TextSecondary, fontSize: 12 }}
              axisLine={{ stroke: c.Border }}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: c.BackgroundTile,
                border: `1px solid ${c.Border}`,
                borderRadius: 8,
              }}
              labelStyle={{ color: c.TextSecondary }}
              itemStyle={{ color: c.White }}
              labelFormatter={(_label, payload) =>
                payload?.[0]?.payload?.fullLabel ?? _label
              }
              cursor={{ fill: "rgba(255, 255, 255, 0.05)" }}
            />
            <Legend
              wrapperStyle={{ color: "rgba(255, 255, 255, 0.7)", fontSize: 12 }}
            />
            {models.map((model, index) => (
              <Bar
                key={model}
                dataKey={model}
                name={
                  t(
                    Resources.Common.VoiceLlms[
                      model as keyof typeof Resources.Common.VoiceLlms
                    ],
                  ) ?? model
                }
                stackId="generations"
                fill={CHART_COLORS[index % CHART_COLORS.length]}
                radius={
                  index === models.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]
                }
                maxBarSize={48}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
};
